import Checkbox from "@/v2-ui/checkbox"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Popover from "@/v2-ui/popover"
import Select from "@/v2-ui/select"
import Textarea from "@/v2-ui/textarea"
import cn from "@/v2-ui/utils/utils.cn"
import Button, {
  TooltipButton,
  INPUT_ALIGNED_BUTTON_CLASSES,
} from "@/v2-backoffice-client/components/button"
import { LAYER_CREATE_TYPE_OPTIONS } from "@/v2-backoffice-client/layer/create/layer.create.constants"
import { FEATURE_CODE_PATTERN } from "@/v2-backoffice-client/feature/feature.constants"
import type { LayerCreateIdentityValue } from "@/v2-backoffice-client/layer/create/layer.create.prefill"
import type { LayerServiceSublayer } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerCreateIdentityProps = {
  identity: LayerCreateIdentityValue
  onIdentityChange(identity: LayerCreateIdentityValue): void
  layerTypeCode: string
  onLayerTypeChange(layerTypeCode: string): void
  /** The probed service's sublayers; shown for multi-layer types. */
  sublayers: LayerServiceSublayer[]
  /** null = every sublayer (service defaults). */
  visibleIds: ReadonlySet<number | string> | null
  onVisibleIdsChange(visibleIds: ReadonlySet<number | string>): void
  /**
   * Asks the AI assistant to (re)propose the identity for the current
   * direction; absent on the manual path (nothing to ground in).
   */
  onAiIdentity?(): void
}

/** Sublayer picking only means something for the composite types. */
const SUBLAYER_TYPES = new Set(["group", "map-image", "wms"])

/**
 * Who the new layer is: feature code (the permanent identifier — everything
 * else can change later), the detected layer type, en-GB name/description
 * seeded from the service, and — for composite types — which sublayers the
 * configuration includes. Type changes re-seed the editor draft; sublayer
 * toggles patch it in place.
 */
function LayerCreateIdentity(props: LayerCreateIdentityProps) {
  const {
    identity,
    onIdentityChange,
    layerTypeCode,
    onLayerTypeChange,
    sublayers,
    visibleIds,
    onVisibleIdsChange,
    onAiIdentity,
  } = props

  const isCodeValid =
    identity.featureCode === "" ||
    FEATURE_CODE_PATTERN.test(identity.featureCode)

  const showSublayers =
    SUBLAYER_TYPES.has(layerTypeCode) && sublayers.length > 1
  const selectedIds =
    visibleIds ?? new Set(sublayers.map((sublayer) => sublayer.id))

  function toggleSublayer(id: number | string, checked: boolean) {
    const next = new Set(selectedIds)
    if (checked) next.add(id)
    else next.delete(id)
    onVisibleIdsChange(next)
  }

  return (
    <div className="flex shrink-0 flex-wrap items-start gap-x-6 gap-y-3">
      <div className="flex min-w-64 flex-1 flex-col gap-1">
        <Label htmlFor="layer-create-feature-code">Feature code</Label>
        <Input
          id="layer-create-feature-code"
          className={cn(
            "font-mono text-xs",
            !isCodeValid && "border-destructive",
          )}
          placeholder="MapLayer_NO_…"
          value={identity.featureCode}
          onChange={(event) =>
            onIdentityChange({ ...identity, featureCode: event.target.value })
          }
        />
        <p className="text-[11px] text-muted-foreground">
          {isCodeValid
            ? "Permanent identifier — everything else can change later."
            : "Letters, digits and underscores only, starting with a letter."}
        </p>
      </div>

      <div className="flex w-40 flex-col gap-1">
        <Label htmlFor="layer-create-type">Type</Label>
        <Select value={layerTypeCode} onValueChange={onLayerTypeChange}>
          <Select.Trigger id="layer-create-type" size="sm">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {LAYER_CREATE_TYPE_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        <p className="text-[11px] text-muted-foreground">
          Changing it re-seeds the configuration.
        </p>
      </div>

      <div className="flex min-w-56 flex-1 flex-col gap-1">
        <Label htmlFor="layer-create-name">Name (en-GB)</Label>
        <Input
          id="layer-create-name"
          value={identity.name}
          onChange={(event) =>
            onIdentityChange({ ...identity, name: event.target.value })
          }
        />
        <p className="text-[11px] text-muted-foreground">
          Customer-facing; other languages via the layer's Details tab after
          creation.
        </p>
      </div>

      <div className="flex min-w-64 flex-[2] flex-col gap-1">
        <span className="flex items-center justify-between gap-2">
          <Label htmlFor="layer-create-description">Description (en-GB)</Label>
          {onAiIdentity && (
            <TooltipButton
              size="tiny"
              variant="outline"
              iconSrc="sparkles"
              iconClassName="text-primary"
              className="-my-0.5"
              content="Ask the AI assistant to re-propose feature code, name and description for the current direction"
              aria-label="Suggest the identity with AI"
              data-feature-code="UI_BackofficeLayers_Create_SuggestIdentity"
              onClick={onAiIdentity}
            >
              Suggest with AI
            </TooltipButton>
          )}
        </span>
        <Textarea
          id="layer-create-description"
          rows={2}
          value={identity.description}
          onChange={(event) =>
            onIdentityChange({ ...identity, description: event.target.value })
          }
        />
      </div>

      {showSublayers && (
        <div className="flex w-40 shrink-0 flex-col gap-1">
          <Label>Sublayers</Label>
          <Popover>
            <Popover.Trigger asChild>
              <Button
                variant="outline"
                size="sm"
                iconSrc="layers"
                className={cn(INPUT_ALIGNED_BUTTON_CLASSES, "justify-start")}
                data-feature-code="UI_BackofficeLayers_Create_Sublayers"
              >
                {selectedIds.size} of {sublayers.length}
              </Button>
            </Popover.Trigger>
            <Popover.Content align="start" className="w-80">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1">
                  <p className="flex-1 text-xs font-medium">
                    Sublayers in the configuration
                  </p>
                  <Button
                    size="tiny"
                    variant="ghost"
                    data-feature-code="UI_BackofficeLayers_Create_SublayersAll"
                    onClick={() =>
                      onVisibleIdsChange(
                        new Set(sublayers.map((sublayer) => sublayer.id)),
                      )
                    }
                  >
                    All
                  </Button>
                  <Button
                    size="tiny"
                    variant="ghost"
                    data-feature-code="UI_BackofficeLayers_Create_SublayersNone"
                    onClick={() => onVisibleIdsChange(new Set())}
                  >
                    None
                  </Button>
                </div>
                <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
                  {sublayers.map((sublayer) => {
                    const id = `layer-create-sublayer-${sublayer.id}`
                    return (
                      <span
                        key={sublayer.id}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          id={id}
                          checked={selectedIds.has(sublayer.id)}
                          onCheckedChange={(checked) =>
                            toggleSublayer(sublayer.id, checked === true)
                          }
                        />
                        <Label
                          htmlFor={id}
                          className="min-w-0 flex-1 cursor-pointer truncate text-xs font-normal"
                          title={String(sublayer.name)}
                        >
                          {sublayer.name}
                        </Label>
                      </span>
                    )
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Toggling patches the configuration draft in place — manual and
                  AI edits survive.
                </p>
              </div>
            </Popover.Content>
          </Popover>
          <p className="text-[11px] text-muted-foreground">
            Which of the service&apos;s layers are included.
          </p>
        </div>
      )}
    </div>
  )
}

export default LayerCreateIdentity
